// Walkthrough — interactive product tour for Field prototype
// Investor/exec focused: emphasizes the "why" and business impact
// Step-by-step tooltips with spotlight dimming effect

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// ============================================================
// Step definitions
// ============================================================

export interface WalkthroughStep {
  id: string;
  /** CSS selector or 'viewport' for full-screen steps */
  target: string;
  /** Where to position the tooltip relative to the target */
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  title: string;
  body: string;
  /** Optional small detail line */
  detail?: string;
  /** If true, dim the background but don't spotlight a specific element */
  fullScreen?: boolean;
  /** Nudge tooltip position */
  offsetX?: number;
  offsetY?: number;
}

const STEPS: WalkthroughStep[] = [
  {
    id: 'welcome',
    target: 'viewport',
    placement: 'center',
    fullScreen: true,
    title: 'Welcome to Field',
    body: 'A spatial workspace where everything you\'re working on lives in one view — organized by the kind of attention it needs, not by app or folder.',
    detail: 'Click "Next" to take a quick tour',
  },
  {
    id: 'four-zones',
    target: 'viewport',
    placement: 'center',
    fullScreen: true,
    title: 'Four Cognitive Zones',
    body: 'Field organizes your work into four zones based on cognitive context: what you\'re actively working on, what needs your attention, what you\'re thinking about, and what\'s in someone else\'s hands.',
    detail: 'This mirrors how your brain actually categorizes work — not how tools force you to organize it',
  },
  {
    id: 'focused-execution',
    target: '[data-zone="focused_execution"]',
    placement: 'right',
    title: 'Working On',
    body: 'Your active work lives here. Deals in negotiation, projects shipping this sprint, proposals being drafted. The biggest, brightest cards are your hottest items.',
    detail: 'Card size and color intensity reflect priority — you can read the room at a glance',
  },
  {
    id: 'reactive-triage',
    target: '[data-zone="reactive_triage"]',
    placement: 'left',
    title: 'Needs Attention',
    body: 'Things demanding your action surface here automatically. Churn risks, silent stakeholders, overdue responses — the system detects patterns that matter before you notice them.',
    detail: 'AI identifies risk patterns like "gone dark for 3 weeks" or "matches historical loss pattern"',
  },
  {
    id: 'reflective-thinking',
    target: '[data-zone="reflective_thinking"]',
    placement: 'right',
    title: 'Thinking About',
    body: 'Strategy and analysis items sit here. Pipeline reviews, competitive analysis, long-term planning. Items that have gone stale get visually cold, reminding you to revisit.',
    detail: 'Cold, dim cards signal neglected priorities — your "strategic debt" becomes visible',
  },
  {
    id: 'collaborative-waiting',
    target: '[data-zone="collaborative_waiting"]',
    placement: 'left',
    title: 'Waiting On',
    body: 'Items in someone else\'s hands. Pending reviews, awaiting approvals, blocked by external stakeholders. You see exactly what\'s stuck and for how long.',
    detail: 'No more "let me check on that" — stalled handoffs become impossible to miss',
  },
  {
    id: 'card-anatomy',
    target: '[data-walkthrough="card-highlight"]',
    placement: 'right',
    title: 'Every Card Tells a Story',
    body: 'Cards adapt to what they represent. Deals show dollar values and health rings. Projects show percent complete. Documents show status. People show engagement. Each card type is designed for instant comprehension.',
    detail: 'One entity can appear in multiple zones with different facets — Morrison is both "Working On" and "Needs Attention"',
  },
  {
    id: 'detail-panel',
    target: '[data-walkthrough="card-highlight"]',
    placement: 'right',
    title: 'Click for Deep Context',
    body: 'Click any card to open a detail panel with AI-generated insights, key documents, suggested next actions, open questions, and risk factors. The panel pushes the canvas aside like a drawer.',
    detail: 'Try clicking a card after the tour to see it in action',
  },
  {
    id: 'toolbar',
    target: '[data-walkthrough="toolbar"]',
    placement: 'top',
    title: 'Act Without Switching',
    body: 'Draft an email, capture a note, start a chat, or create a task — all without leaving this view. AI pre-fills context from whatever you\'re looking at.',
    detail: 'The toolbar is draggable — dock it to any screen edge',
  },
  {
    id: 'search',
    target: '[data-walkthrough="header-search"]',
    placement: 'bottom',
    title: 'Find Anything Instantly',
    body: 'Search across all units, people, deals, and documents. Results filter the field in real-time — matching cards stay vivid, everything else dims.',
    detail: 'Keyboard shortcut: ⌘K',
  },
  {
    id: 'modes',
    target: '[data-walkthrough="mode-switcher"]',
    placement: 'bottom',
    title: 'Multiple Ways to See',
    body: 'Field is just one view. Switch to Graph for relationship visualization, Timeline for temporal analysis, or List for structured data. Same knowledge, different lenses.',
    detail: 'The "Prismatic" concept — viewing information from multiple perspectives',
  },
  {
    id: 'closing',
    target: 'viewport',
    placement: 'center',
    fullScreen: true,
    title: 'Never Lose Context Again',
    body: 'Field transforms scattered information across apps and inboxes into a single spatial workspace that matches how you actually think. Every conversation becomes searchable, every insight connected, every collaboration seamless.',
    detail: 'Explore the prototype — click cards, try the toolbar, drag cards between zones, search with ⌘K',
  },
];

// ============================================================
// Spotlight overlay
// ============================================================

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getElementRect(selector: string, padding = 12): SpotlightRect | null {
  if (selector === 'viewport') return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - padding,
    left: r.left - padding,
    width: r.width + padding * 2,
    height: r.height + padding * 2,
  };
}

function getTooltipPosition(
  rect: SpotlightRect | null,
  placement: WalkthroughStep['placement'],
  offsetX = 0,
  offsetY = 0,
): React.CSSProperties {
  if (!rect || placement === 'center') {
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const gap = 16;

  switch (placement) {
    case 'right':
      return {
        top: rect.top + rect.height / 2 + offsetY,
        left: rect.left + rect.width + gap + offsetX,
        transform: 'translateY(-50%)',
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2 + offsetY,
        right: window.innerWidth - rect.left + gap - offsetX,
        transform: 'translateY(-50%)',
      };
    case 'bottom':
      return {
        top: rect.top + rect.height + gap + offsetY,
        left: rect.left + rect.width / 2 + offsetX,
        transform: 'translateX(-50%)',
      };
    case 'top':
      return {
        bottom: window.innerHeight - rect.top + gap - offsetY,
        left: rect.left + rect.width / 2 + offsetX,
        transform: 'translateX(-50%)',
      };
    default:
      return {};
  }
}

// ============================================================
// Main Walkthrough Component
// ============================================================

interface WalkthroughProps {
  onComplete: () => void;
}

export default function Walkthrough({ onComplete }: WalkthroughProps) {
  const [step, setStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const animationRef = useRef<number | null>(null);

  // Use refs for step-dependent values so event handlers are never stale
  const stepRef = useRef(step);
  stepRef.current = step;
  const exitingRef = useRef(exiting);
  exitingRef.current = exiting;

  const totalSteps = STEPS.length;

  const currentStep = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const progress = ((step + 1) / STEPS.length) * 100;

  // Fade in on mount
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Update spotlight position when step changes
  useEffect(() => {
    const update = () => {
      const s = STEPS[stepRef.current];
      if (s) {
        const rect = getElementRect(s.target);
        setSpotlightRect(rect);
      }
      animationRef.current = requestAnimationFrame(update);
    };
    update();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []); // Run once — reads stepRef.current each frame

  // Navigation functions that use refs (never stale)
  const goNext = () => {
    if (exitingRef.current) return;
    if (stepRef.current >= totalSteps - 1) {
      setExiting(true);
      setTimeout(onComplete, 400);
    } else {
      setStep(s => s + 1);
    }
  };

  const goBack = () => {
    if (exitingRef.current) return;
    if (stepRef.current > 0) setStep(s => s - 1);
  };

  const goSkip = () => {
    if (exitingRef.current) return;
    setExiting(true);
    setTimeout(onComplete, 400);
  };

  // Keyboard navigation — single stable effect, never stale
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        goSkip();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Stable — uses refs internally

  const tooltipPos = getTooltipPosition(
    spotlightRect,
    currentStep.placement,
    currentStep.offsetX,
    currentStep.offsetY,
  );

  // SVG mask for spotlight cutout
  const svgMask = spotlightRect ? (
    <svg
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}
    >
      <defs>
        <mask id="spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <rect
            x={spotlightRect.left}
            y={spotlightRect.top}
            width={spotlightRect.width}
            height={spotlightRect.height}
            rx={14}
            fill="black"
          />
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(0, 0, 0, 0.6)"
        mask="url(#spotlight-mask)"
      />
    </svg>
  ) : null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        fontFamily: '-apple-system, system-ui, sans-serif',
        opacity: visible && !exiting ? 1 : 0,
        transition: 'opacity 0.4s ease',
        pointerEvents: exiting ? 'none' : 'auto',
      }}
    >
      {/* Dim overlay — either full or with spotlight cutout */}
      {spotlightRect ? (
        svgMask
      ) : (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            zIndex: 10000,
          }}
        />
      )}

      {/* Clickable backdrop to advance */}
      <div
        onClick={goNext}
        style={{
          position: 'fixed',
          inset: 0,
          cursor: 'pointer',
          zIndex: 10001,
        }}
      />

      {/* Spotlight ring glow */}
      {spotlightRect && (
        <div style={{
          position: 'fixed',
          top: spotlightRect.top - 2,
          left: spotlightRect.left - 2,
          width: spotlightRect.width + 4,
          height: spotlightRect.height + 4,
          borderRadius: 16,
          border: '2px solid #4DC4D2',
          pointerEvents: 'none',
          zIndex: 10001,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }} />
      )}

      {/* Tooltip card — must be above everything including R3F Html overlays */}
      {/* Outer wrapper: solid opaque backing to block R3F Html bleed-through */}
      <div
        key={step}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          ...tooltipPos,
          zIndex: 10002,
          width: currentStep.fullScreen ? 480 : 360,
          isolation: 'isolate',
        }}
      >
        {/* Solid opaque backing — sits behind content, blocks everything */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: '#FFFFFF',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgb(0 0 0 / 0.35), 0 0 0 1px rgb(0 0 0 / 0.12)',
          zIndex: 0,
        }} />

        {/* Content layer on top of the solid backing */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          borderRadius: 16,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
        }}>
          {/* Progress bar */}
          <div style={{
            height: 3,
            backgroundColor: '#F0F1F3',
          }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              backgroundColor: '#4DC4D2',
              borderRadius: 2,
              transition: 'width 0.4s ease',
            }} />
          </div>

          {/* Content */}
          <div style={{ padding: '24px 28px 16px', backgroundColor: '#FFFFFF' }}>
            {/* Step counter */}
            <div style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#4DC4D2',
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 8,
            }}>
              {step + 1} of {STEPS.length}
            </div>

            <h3 style={{
              margin: 0,
              fontSize: currentStep.fullScreen ? 22 : 18,
              fontWeight: 700,
              color: '#111318',
              lineHeight: 1.3,
              marginBottom: 10,
            }}>
              {currentStep.title}
            </h3>

            <p style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.6,
              color: '#4A4F5C',
            }}>
              {currentStep.body}
            </p>

            {currentStep.detail && (
              <p style={{
                margin: '10px 0 0',
                fontSize: 12,
                lineHeight: 1.5,
                color: '#7A7F8E',
                fontStyle: 'italic',
              }}>
                {currentStep.detail}
              </p>
            )}
          </div>

          {/* Footer with navigation */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 28px 20px',
            backgroundColor: '#FFFFFF',
          }}>
            <button
              onClick={goSkip}
              style={{
                border: 'none',
                backgroundColor: '#FFFFFF',
                cursor: 'pointer',
                fontSize: 12,
                color: '#A3A8B4',
                fontFamily: 'inherit',
                padding: '4px 0',
              }}
            >
              Skip tour
            </button>

            <div style={{ display: 'flex', gap: 8 }}>
              {!isFirst && (
                <button
                  onClick={goBack}
                  style={{
                    border: '1px solid #E3E5EB',
                    backgroundColor: '#FFFFFF',
                    borderRadius: 8,
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#4A4F5C',
                    fontFamily: 'inherit',
                  }}
                >
                  Back
                </button>
              )}
              <button
                onClick={goNext}
                style={{
                  border: 'none',
                  backgroundColor: isLast ? '#2563EB' : '#111318',
                  borderRadius: 8,
                  padding: '8px 20px',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  fontFamily: 'inherit',
                }}
              >
                {isLast ? 'Start Exploring' : 'Next'}
              </button>
            </div>
          </div>

          {/* Keyboard hint */}
          <div style={{
            textAlign: 'center',
            paddingBottom: 12,
            fontSize: 10,
            color: '#C0C4CC',
            backgroundColor: '#FFFFFF',
          }}>
            Use arrow keys or Enter to navigate · Esc to skip
          </div>
        </div>
      </div>

      {/* Step dots */}
      <div style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 6,
        zIndex: 10003,
      }}>
        {STEPS.map((s, i) => (
          <button
            key={s.id}
            onClick={(e) => { e.stopPropagation(); setStep(i); }}
            style={{
              width: i === step ? 20 : 8,
              height: 8,
              borderRadius: 4,
              border: 'none',
              backgroundColor: i === step
                ? '#4DC4D2'
                : i < step
                ? '#7DD8E2'
                : '#666666',
              cursor: 'pointer',
              padding: 0,
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
    </div>,
    document.body,
  );
}
